import { useSuspenseQueries } from '@tanstack/react-query';
import { Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { AutocompleteMonoSelect } from 'components/AutocompleteMonoSelect';
import type { UnassignedCustomFieldsQueryOptions } from 'services/custom-fields/customFieldsQueries';
import type { CustomField } from 'services/custom-fields/schemas';

const CUSTOM_FIELDS_SETTINGS_PATH = '/settings/custom-fields';

type AssignCustomFieldDropdownLoaderProps = {
  onAssign: (field: CustomField) => void;
  query: UnassignedCustomFieldsQueryOptions;
  placeholder: string;
  ariaLabel: string;
  createNewLabel: string;
};

function AssignCustomFieldDropdownLoader({
  onAssign,
  query,
  placeholder,
  ariaLabel,
  createNewLabel,
}: AssignCustomFieldDropdownLoaderProps) {
  const theme = useTheme();
  const customFields = useSuspenseQueries(query);
  const getOptionLabel = useCallback((option: CustomField) => option.friendlyName, []);

  const handleCreateNew = useCallback(() => {
    window.open(CUSTOM_FIELDS_SETTINGS_PATH, '_blank', 'noopener,noreferrer');
  }, []);

  const footer = (
    <Box
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleCreateNew}
      data-dd-action-name="Go to Custom Fields Settings Page"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        minHeight: theme.spacing(4.5),
        px: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {createNewLabel}
    </Box>
  );

  const maxHeight = `calc(${theme.spacing(4.5 * 4)} + ${theme.spacing(2)})`; // 4 items + padding

  return (
    <AutocompleteMonoSelect<CustomField>
      options={customFields}
      getOptionLabel={getOptionLabel}
      onSelect={onAssign}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      footer={footer}
      maxHeight={maxHeight}
    />
  );
}

export function AssignCustomFieldDropdown({
  listUnassignedQuery,
  onAssign,
}: {
  listUnassignedQuery: UnassignedCustomFieldsQueryOptions;
  onAssign: (field: CustomField) => void;
}) {
  const { t } = useTranslation();

  const placeholder = t('editor.variables.enter_field_name');
  const ariaLabel = t('editor.variables.assign_custom_field', 'Assign a custom field');
  const createNewLabel = t('editor.variables.create_new_custom_field');

  return (
    <Suspense
      fallback={
        <AutocompleteMonoSelect options={[]} getOptionLabel={() => ''} disabled loading placeholder={placeholder} ariaLabel={ariaLabel} />
      }
    >
      <AssignCustomFieldDropdownLoader
        onAssign={onAssign}
        query={listUnassignedQuery}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        createNewLabel={createNewLabel}
      />
    </Suspense>
  );
}
